import api from './api';

/**
 * AI Service - Semantic search, similar streams, and recommendations
 *
 * Uses the Tempest AI Platform for vector-based search and similarity
 */

export interface AISearchResult {
  id: string;
  score: number;
  payload: {
    stream_id?: string;
    title?: string;
    artist?: string;
    genre?: string;
    description?: string;
    cover_image?: string;
    audio_url?: string;
    created_by?: string;
    created_at?: number;
    [key: string]: unknown;
  };
}

export interface AIHealthStatus {
  status: 'ok' | 'degraded';
  functions: Record<string, boolean>;
}

/**
 * Vinyl match result from "Dig Deeper" feature
 * Connects Radio streams to Record collection via Qdrant
 */
export interface VinylMatch {
  id: string;
  score: number;
  title: string;
  artist?: string;
  genre?: string;
  coverImage?: string;
  pressYear?: number;      // Year of this pressing/reissue
  originalYear?: number;   // Original recording year (from Discogs master)
  appSource: string;
}

export interface VinylMatchResponse {
  matches: VinylMatch[];
  streamId: string;
  userId: string;
  hasMatches: boolean;
}

export interface VinylCollectionResponse {
  vinyl: VinylMatch[];
  count: number;
  userId: string;
}

/**
 * TEM-1529 — the answer of GET /ai/query: the shared query grammar (parsed in
 * the backend by the Rust grammar compiled to wasm) run over Radio's own shows
 * and their set tracks. Mirrors `RadioQueryAnswer` in
 * apps/radio/backend/src/services/radioQueryService.ts.
 */
export type RadioQueryItem =
  | {
      kind: 'show';
      streamId: string;
      title: string;
      artist?: string;
      coverImage?: string;
      genre?: string;
    }
  | {
      kind: 'track';
      streamId: string;
      streamName: string;
      setId: string;
      segmentId: string;
      title?: string;
      artist?: string;
      trackNumber?: number;
      startTimeMs?: number;
      /** The set track's mood (never its secondary mood). */
      mood?: string;
      /** Who wrote `mood`: 'classifier' is an AI guess from the artist and title. */
      moodSource?: string;
    };

export interface RadioQueryAnswer {
  /** The canonical text when the query parsed, else the text as typed. */
  query: string;
  syntax: boolean;
  grain: 'album' | 'track' | null;
  grainExplicit: boolean;
  /** What one row is: a show or a set track. Null for a refusal. */
  rows: 'shows' | 'tracks' | null;
  total: number;
  items: RadioQueryItem[];
  /** Grammar fields this query used that Radio cannot honour. */
  notAvailable: string[];
  /** Sentences to show beside the results — every not-available field has one. */
  notes: string[];
  /** The grammar's refusal, as it worded it. */
  error: { kind: string; field: string | null; message: string } | null;
}

/**
 * One line saying what the answer holds — "12 shows (grain inferred)". An
 * inferred grain that is not shown reads as a bug (design §3.2), so the
 * "(grain inferred)" is not decoration. Empty for a refusal.
 */
export function describeQueryAnswer(answer: RadioQueryAnswer): string {
  if (answer.rows === null) return '';
  const noun = answer.rows === 'tracks' ? 'set track' : 'show';
  const plural = answer.total === 1 ? noun : `${noun}s`;
  const how = answer.grainExplicit ? '' : ' (grain inferred)';
  return `${answer.total.toLocaleString()} ${plural}${how}`;
}

export const aiService = {
  /**
   * Check AI service health
   */
  async healthCheck(): Promise<AIHealthStatus> {
    return api.get<AIHealthStatus>('/ai/health');
  },

  /**
   * Semantic search for streams by natural language query
   * @param query - Natural language search query (e.g., "chill jazz vibes")
   * @param limit - Maximum results to return (default: 10)
   */
  async semanticSearch(query: string, limit: number = 10): Promise<AISearchResult[]> {
    // api.get already unwraps response.data.data, so we get the array directly
    return api.get<AISearchResult[]>(`/ai/search?q=${encodeURIComponent(query)}&limit=${limit}`);
  },

  /**
   * Run text that uses the query grammar's syntax (TEM-1529). Plain words go
   * to `semanticSearch`; `utils/querySyntax.ts` decides which.
   * @param query - The text as typed, e.g. `artist:coltrane grain:track`
   * @param limit - Maximum rows (the backend caps it at 100)
   */
  async query(query: string, limit: number = 20): Promise<RadioQueryAnswer> {
    return api.get<RadioQueryAnswer>(`/ai/query?q=${encodeURIComponent(query)}&limit=${limit}`);
  },

  /**
   * Find streams similar to a given stream
   * @param streamId - Source stream ID
   * @param limit - Maximum results to return (default: 10)
   */
  async findSimilarStreams(streamId: string, limit: number = 10): Promise<AISearchResult[]> {
    // api.get already unwraps response.data.data, so we get the array directly
    return api.get<AISearchResult[]>(`/ai/similar/${streamId}?limit=${limit}`);
  },

  /**
   * Get personalized recommendations
   * @param genres - Optional genre filters
   * @param limit - Maximum results to return (default: 10)
   */
  async getRecommendations(genres?: string[], limit: number = 10): Promise<AISearchResult[]> {
    const params = new URLSearchParams();
    params.set('limit', limit.toString());
    if (genres && genres.length > 0) {
      params.set('genres', genres.join(','));
    }
    // api.get already unwraps response.data.data, so we get the array directly
    return api.get<AISearchResult[]>(`/ai/recommendations?${params.toString()}`);
  },

  /**
   * Index a stream in the vector database (authenticated)
   * @param streamId - Stream ID to index
   */
  async indexStream(streamId: string): Promise<{ indexed: boolean; streamId: string }> {
    return api.post<{ indexed: boolean; streamId: string }>(`/ai/index/${streamId}`, {});
  },

  /**
   * Remove a stream from the vector index (authenticated)
   * @param streamId - Stream ID to remove
   */
  async removeFromIndex(streamId: string): Promise<{ deleted: boolean; streamId: string }> {
    return api.delete<{ deleted: boolean; streamId: string }>(`/ai/index/${streamId}`);
  },

  /**
   * Find vinyl records in user's collection that match a stream
   * This is the "Dig Deeper" feature - connects Radio to Record
   * @param streamId - Stream to find matches for
   * @param limit - Maximum results (default: 5)
   */
  async findVinylMatches(streamId: string, limit: number = 5): Promise<VinylMatchResponse> {
    return api.get<VinylMatchResponse>(`/ai/vinyl-match/${streamId}?limit=${limit}`);
  },

  /**
   * Get user's vinyl collection from Qdrant (authenticated)
   * @param limit - Maximum results (default: 50)
   */
  async getUserVinylCollection(limit: number = 50): Promise<VinylCollectionResponse> {
    return api.get<VinylCollectionResponse>(`/ai/my-vinyl?limit=${limit}`);
  }
};

export default aiService;
